'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import ProductCard from '@/components/ProductCard';
import CartDrawer from '@/components/cart/CartDrawer';
import { useCart } from '@/context/CartContext';
import { GAME_CATEGORIES, ALL_PRODUCTS } from '@/lib/products';
import { useTheme } from '@/context/ThemeContext';
import { useI18n } from '@/i18n/LocaleProvider';
import { LOCALES } from '@/i18n/config';
import { localizeCategory, localizeProduct } from '@/i18n/products';

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [activeGame, setActiveGame] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { theme, toggleTheme } = useTheme();
  const { locale, t } = useI18n();
  const { itemCount, openDrawer } = useCart();
  const categories = GAME_CATEGORIES.map(cat => localizeCategory(cat, locale));

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (menuOpen || searchOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen, searchOpen]);

  useEffect(() => {
    if (searchOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      setInputValue('');
      setSearchQuery('');
      setActiveGame('');
      setHasSearched(false);
    }
  }, [searchOpen]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSearchOpen(false);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  const closeMenu = () => setMenuOpen(false);

  const handleSearch = useCallback(() => {
    if (inputValue.trim() || activeGame) {
      setSearchQuery(inputValue.trim());
      setHasSearched(true);
    }
  }, [inputValue, activeGame]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleGameFilter = (slug: string) => {
    const next = activeGame === slug ? '' : slug;
    setActiveGame(next);
    if (hasSearched) {
      setHasSearched(true);
    }
  };

  const filtered = (hasSearched || activeGame) ? ALL_PRODUCTS?.map(p => localizeProduct(p, locale)).filter(p => {
    const matchesGame = activeGame ? p?.gameSlug === activeGame : true;
    if (!searchQuery) return matchesGame;
    const q = searchQuery.toLowerCase();
    return matchesGame && (
      p?.name?.toLowerCase()?.includes(q) ||
      p?.game?.toLowerCase()?.includes(q) ||
      p?.set?.toLowerCase()?.includes(q) ||
      p?.type?.toLowerCase()?.includes(q) ||
      p?.description?.toLowerCase()?.includes(q)
    );
  }) : [];

  return (
    <>
      {/* Sticky top wrapper */}
      <div className="fixed top-0 left-0 w-full z-50">
        <nav
          className={`w-full transition-all duration-500 ${scrolled ? 'bg-background/90 backdrop-blur-xl border-b border-border' : 'bg-background/40 backdrop-blur-md'}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-2.5 group" onClick={closeMenu}>
              <AppLogo size={36} />
              <span className="font-display font-bold text-lg tracking-tight text-foreground group-hover:text-primary transition-colors">
                TCGSecret
              </span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <Link href="/" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                {t.nav.home}
              </Link>
              <Link href="/preorders" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                {t.nav.preorders}
              </Link>
              <Link href="/#about" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                {t.nav.about}
              </Link>
              <Link href="/#contact" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                {t.nav.contact}
              </Link>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-3">
              {/* Search button */}
              <button
                onClick={() => setSearchOpen(true)}
                className="w-9 h-9 flex items-center justify-center rounded-full glass-card border border-border hover:border-primary/40 text-muted-foreground hover:text-foreground transition-all duration-200"
                aria-label={t.header.searchAria}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>

              {/* Language switcher (in the mobile menu on small screens) */}
              <LanguageSwitcher className="hidden sm:flex" />

              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                className="w-9 h-9 flex items-center justify-center rounded-full glass-card border border-border hover:border-primary/40 text-muted-foreground hover:text-foreground transition-all duration-200"
                aria-label={theme === 'dark' ? t.header.lightMode : t.header.darkMode}
              >
                {theme === 'dark' ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                )}
              </button>

              {/* Cart */}
              <button
                onClick={() => { closeMenu(); openDrawer(); }}
                className="relative w-9 h-9 flex items-center justify-center rounded-full glass-card border border-border hover:border-primary/40 text-muted-foreground hover:text-foreground transition-all duration-200"
                aria-label={t.cart.open(itemCount)}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold">
                    {itemCount}
                  </span>
                )}
              </button>

              {/* Mobile hamburger */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="md:hidden w-10 h-10 flex flex-col items-center justify-center gap-1.5 rounded-lg"
                aria-label={menuOpen ? t.header.closeMenu : t.header.openMenu}
              >
                <span className={`block w-5 h-0.5 bg-foreground transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} />
                <span className={`block w-5 h-0.5 bg-foreground transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`} />
                <span className={`block w-5 h-0.5 bg-foreground transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
              </button>
            </div>
          </div>

          {/* Category bar */}
          <div className="border-t border-border/60">
            <div
              className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-11 flex items-center gap-2 overflow-x-auto"
              style={{ msOverflowStyle: 'none', scrollbarWidth: 'none' }}
            >
              <Link
                href="/category"
                className="flex-shrink-0 px-3.5 py-1 rounded-full text-xs font-bold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                {t.nav.allTcgs}
              </Link>
              {categories.map(cat => (
                <Link
                  key={cat?.id}
                  href={`/category?game=${cat?.slug}`}
                  className="flex-shrink-0 flex items-center gap-1.5 px-3.5 py-1 rounded-full text-xs font-semibold glass-card border border-border text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: cat?.color }} />
                  {cat?.shortName}
                </Link>
              ))}
            </div>
          </div>
        </nav>
      </div>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 transition-all duration-500 ${menuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
        style={{ background: theme === 'dark' ? 'rgba(10, 6, 8, 0.97)' : 'rgba(248, 245, 255, 0.97)', backdropFilter: 'blur(20px)', paddingTop: '140px' }}
      >
        <div className="flex flex-col h-full overflow-y-auto p-6">
          <div className="flex flex-col gap-2 mb-8">
            <Link href="/" onClick={closeMenu} className="py-4 border-b border-border text-2xl font-bold text-foreground hover:text-primary transition-colors">{t.nav.home}</Link>
            <Link href="/category" onClick={closeMenu} className="py-4 border-b border-border text-2xl font-bold text-foreground hover:text-primary transition-colors">{t.nav.allCategories}</Link>
            <Link href="/preorders" onClick={closeMenu} className="py-4 border-b border-border text-2xl font-bold text-foreground hover:text-primary transition-colors">{t.nav.preorders}</Link>
            <Link href="/#about" onClick={closeMenu} className="py-4 border-b border-border text-2xl font-bold text-foreground hover:text-primary transition-colors">{t.nav.aboutUs}</Link>
            <Link href="/#contact" onClick={closeMenu} className="py-4 border-b border-border text-2xl font-bold text-foreground hover:text-primary transition-colors">{t.nav.contact}</Link>
          </div>
          <div className="flex items-center justify-between mb-6">
            <span className="text-sm font-semibold text-muted-foreground">{t.header.language}</span>
            <LanguageSwitcher />
          </div>
          <div className="grid grid-cols-2 gap-3">
            {categories.map(cat => (
              <Link
                key={cat?.id}
                href={`/category?game=${cat?.slug}`}
                onClick={closeMenu}
                className="glass-card rounded-xl p-3 flex items-center gap-2"
              >
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: cat?.color }} />
                <span className="text-sm font-medium text-foreground">{cat?.shortName}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <CartDrawer />

      {/* Search Overlay */}
      <div
        className={`fixed inset-0 z-[60] transition-all duration-300 ${searchOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
        style={{ background: theme === 'dark' ? 'rgba(10, 6, 8, 0.92)' : 'rgba(248, 245, 255, 0.92)', backdropFilter: 'blur(24px)' }}
        onClick={(e) => { if (e.target === e.currentTarget) setSearchOpen(false); }}
      >
        <div className={`w-full max-w-3xl mx-auto px-4 pt-28 pb-8 transition-all duration-300 ${searchOpen ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'}`}>
          {/* Close button */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-foreground">{t.header.searchTitle}</h2>
            <button
              onClick={() => setSearchOpen(false)}
              className="w-9 h-9 flex items-center justify-center rounded-full glass-card border border-border text-muted-foreground hover:text-foreground transition-colors"
              aria-label={t.header.closeSearch}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Search input + button */}
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t.header.searchPlaceholder}
                className="w-full pl-12 pr-4 py-4 rounded-2xl glass-card border border-border focus:border-primary/60 focus:outline-none text-foreground placeholder:text-muted-foreground text-base transition-colors bg-transparent"
              />
            </div>
            <button
              onClick={handleSearch}
              className="px-6 py-4 rounded-2xl bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 transition-colors flex-shrink-0"
            >
              {t.header.searchButton}
            </button>
          </div>

          {/* TCG filter pills */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-6" style={{ msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
            <button
              onClick={() => { setActiveGame(''); if (hasSearched) setHasSearched(true); }}
              className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                !activeGame ? 'bg-primary text-primary-foreground' : 'glass-card text-muted-foreground hover:text-foreground'
              }`}
            >
              {t.nav.allTcgs}
            </button>
            {categories.map(cat => (
              <button
                key={cat?.id}
                onClick={() => handleGameFilter(cat?.slug)}
                className={`flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                  activeGame === cat?.slug ? 'text-background font-bold' : 'glass-card text-muted-foreground hover:text-foreground'
                }`}
                style={activeGame === cat?.slug ? { backgroundColor: cat?.color } : {}}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: activeGame === cat?.slug ? 'rgba(0,0,0,0.4)' : cat?.color }} />
                {cat?.shortName}
              </button>
            ))}
          </div>

          {/* Results */}
          <div className="overflow-y-auto max-h-[55vh]">
            {!hasSearched && !activeGame ? (
              <p className="text-center text-muted-foreground py-10 text-sm">
                {t.header.searchHint}
              </p>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 glass-card rounded-2xl">
                <div className="text-4xl mb-3">🔍</div>
                <p className="text-xl font-bold text-foreground mb-1">{t.common.noProducts}</p>
                <p className="text-muted-foreground text-sm">{t.header.noResultsHint}</p>
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-foreground mb-4">
                  {t.results(filtered.length, searchQuery, categories.find(c => c.slug === activeGame)?.name)}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filtered.map(product => (
                    <div key={product?.id} onClick={() => setSearchOpen(false)}>
                      <ProductCard product={product} />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function LanguageSwitcher({ className = '' }: { className?: string }) {
  const { locale, t, setLocale } = useI18n();

  return (
    <div
      role="group"
      aria-label={t.header.language}
      className={`h-9 flex items-center rounded-full glass-card border border-border p-0.5 ${className}`}
    >
      {LOCALES.map(code => (
        <button
          key={code}
          onClick={() => setLocale(code)}
          aria-pressed={locale === code}
          className={`h-full px-2.5 rounded-full text-xs font-bold uppercase transition-colors ${
            locale === code ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {code}
        </button>
      ))}
    </div>
  );
}
